import { AllyNode } from "../types";
import { AllyBase } from "./base";

/**
 * Objects of this class house the hierarchy for an attribute.
 * 
 * @extends AllyBase
 * @see AllyAttribute
 */
class AllyAttributeInstance extends AllyBase {
    /**
     * The createIndex function provided by the AllyAttribute object.
     * @see AllyAttribute
     */
    private createIndex: AllyAttribute["createIndex"];
    /**
     * The remove callback provided by the AllyAttribute object.
     * @see AllyAttribute
     */
    private remove: AllyAttribute["remove"];
    /**
     * The list of values and indexes that are part of the hierarchy.
     */
    private values: {index: AllyAttribute["index"], value: string|null}[];
    /**
     * The node the attribute changes are to be applied to.
     */
    private node: AllyNode;
    /**
     * The name of the attribute.
     */
    private name: string;
    /**
     * If this property is True then the current value of the attribute will be saved in the hierarchy when setAttribute is triggered.
     * 
     * The value of this property will be set to False once this has occurred.
     * 
     * This value should only be True if no value is save in the hierarchy.
     */
    private firstWrite: boolean;
    /**
     * If a value was saved in the heirarchy due to AllyAttributeInstance["firstWrite"] then this property will be True.
     */
    private initialValue: boolean;
    /**
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @param createIndex The createIndex function provided by the AllyAttribute object.
     * @param remove The remove callback provided by the AllyAttribute object.
     * @param node The node the attribute changes are to be applied to.
     * @param name The name of the attribute.
     */
    constructor(bridge: () => AllyBase, createIndex: AllyAttribute["createIndex"], remove: AllyAttribute["remove"],  node: AllyNode, name: string) {
        super(bridge);
        this.createIndex = createIndex;
        this.remove = remove;
        this.values = [];
        this.node = node;
        this.name = name;
        this.firstWrite = true;
        this.initialValue = false;
        this.destroyer.addCallback(() => {
            if (this.initialValue) {
                this.values = [this.values[0]];
            } else {
                this.values = [];
            }
            this.writeAttribute();
            this.remove(this.name);
        });
    }
    /**
     * Sets or unsets the value of this attribute.
     * @param index 
     * The index object that will be used to set the Value.
     * 
     * Values set using different index objects will overload each other based on their hierarchy.
     * 
     * The higher the index of the index object is the higher is the priority of the index object when overloading the values set via other index objects.
     * 
     * Warning: The value set via an index object can only be changed using that same index object, otherwise it can only be overloaded with a higher index object.
     * @param value
     * The value the attribute will be set to.
     * 
     * If the value is null then the attribute will be removed from the node while this index object is not overloaded.
     * 
     * If the value is undefined the index object will be removed from the hierarchy. Another value will be used if present. The index can still be used reintroducing it to the hierarchy.
     */
    public setAttribute(index: {index: number, indexKey: {}}, value: string|null|undefined) {
        let i = this.values.findIndex((element) => element.index === index);
        if (i !== -1) {
            if (value === undefined) {
                this.values.splice(i, 1);
                this.updateIndexs();
            } else {
                this.values[i].value = value;
            }
        } else {
            if (value !== undefined) {
                this.values.splice(index.index, 0, {index: index, value: value});
                this.updateIndexs();
            }
        }
        this.writeAttribute();
    }
    /**
     * Updates the indexes of the index objects saved in the AllyAttributeInstance["values"] list according to the order of the list.
     */
    private updateIndexs() {
        for (let i = 0; i < this.values.length; i++) {
            this.values[i].index.index = i;
        }
    }
    /**
     * Writes the value that has the highest index in the AllyAttributeInstance["values"] list into the attribute of the node of this AllyAttributeInstance object.
     */
    private writeAttribute() {
        if (this.firstWrite) {
            this.firstWrite = false;
            let value = this.node.getAttribute ? this.node.getAttribute(this.name) : null;
            if (value !== null) {
                this.values.splice(0,0,{index: this.createIndex(0), value: value});
                this.initialValue = true;
            }
            this.updateIndexs();
        }
        if (this.values.length > 0) {
            let value = this.values[this.values.length - 1].value;
            if (value !== null) {
                this.node.setAttribute && this.node.setAttribute(this.name, value);
            } else {
                this.node.removeAttribute && this.node.removeAttribute(this.name);
            }
        } else {
            this.node.removeAttribute && this.node.removeAttribute(this.name);
        }
    }
}

/**
 * This class is used to change the attributes of DOM nodes.
 * 
 * Each attribute can retain multiple value which overload each other.
 * @extends AllyBase
 * @see AllyAttributeInstance
 */
export class AllyAttribute extends AllyBase {
    /**
     * The dictionary of all the attributes that are set via this AllyAttribute objects.
     * 
     * @see AllyAttributeInstance
     */
    private attributes: {[key: string]: AllyAttributeInstance};
    /**
     * This object is used as a key to ensure only indexes that were created via AllyAttribute["createIndex"] are used.
     */
    private indexKey: {};
    /**
     * The node the attribute changes are to be applied to.
     */
    private node: AllyNode;
    /**
     * A dummy used only to provide the type for the index object without additional imports.
     */
    public index: {index: number, indexKey: {}};
    /**
     * The remove callback that is provided to the created AllyAttributeInstance object.
     */
    private remove: (name: string) => void;

    /**
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @param node The node the attribute changes are to be applied to.
     */
    constructor(bridge: () => AllyBase, node: AllyNode) {
        super(bridge);
        this.attributes = {};
        this.indexKey = {};
        this.node = node;
        this.index = null as  unknown as AllyAttribute["index"];
        this.remove = (name: string) => {
            delete this.attributes[name];
        };
        this.destroyer.addCallback(() => {
            this.reset();
            this.indexKey = undefined as unknown as {};
        });
    }
    /**
     * Sets or unsets the value of an attribute.
     * @param key The key of the attribute.
     * @param index 
     * The index object that will be used to set the Value.
     * 
     * Values set using different index objects will overload each other based on their hierarchy.
     * 
     * The higher the index of the index object is the higher is the priority of the index object when overloading the values set via other index objects.
     * 
     * Warning: The value set via an index object can only be changed using that same index object, otherwise it can only be overloaded with a higher index object.
     * @param value
     * The value the attribute will be set to.
     * 
     * If the value is null then the attribute will be removed from the node while this index object is not overloaded.
     * 
     * If the value is undefined the index object will be removed from the hierarchy. Another value will be used if present. The index can still be used reintroducing it to the hierarchy.
     */
    public setAttribute(key: string, index: {index: number, indexKey: {}}, value: string|null|undefined) {
        if (index.indexKey && index.indexKey === this.indexKey) {
            if (this.attributes[key] === undefined) {
                this.attributes[key] = new AllyAttributeInstance(this.bridge, this.createIndex.bind(this), this.remove, this.node, key);
            }
            this.attributes[key].setAttribute(index, value);
        } else {
            let error = new Error('Invalid index: Use the "createIndex" function to create an index.');
            throw error;
        }
    }
    /**
     * Creates an index object.
     * @param index
     * The base index for the index object that is to be created.
     * 
     * The index of the index object may change.
     * 
     * If an index object with the same index as the newly created index object exists, then the older object will be displaced. The older object will have a higher rank in the hierarchy.
     * 
     * The higher the index of the index object is the higher is the priority of the index object when overloading the values set via other index objects.
     * 
     * Warning: The value set via an index object can only be changed using that same index object, otherwise it can only be overloaded with a higher index object.
     * @returns The created index object.
     */
    public createIndex(index: number) {
        return {index: index, indexKey: this.indexKey};
    }
    /**
     * Resets all attributes to the values that they had when this AllyAttribute object was created.
     */
    public reset() {
        Object.keys(this.attributes).forEach((key) => {
            this.attributes[key].destroy();
        });
    }
}